import { colorForSeat, type GomokuState } from '../games/gomoku';
import type { BoardEntry, Side } from '../lib/match-snapshot';
import { ChessPieceGlyph } from './board/ChessPieceGlyph';
import './BoardSideLabel.css';

export type ToolSample = { vessel: 'bowl' | 'tray' | 'tile'; tool: 'stone' | 'disc' | 'marble' | 'peg' | 'line' | 'pawn' | 'queen'; tone: 'black' | 'white' | 'red'; label: string };

/** A fixed colour sample, never an inventory, mover, or inferred spectator side. */
export function boardSideLabel(entry: BoardEntry, yourSide: Side | null): ToolSample | null {
  if (yourSide !== 'First' && yourSide !== 'Second') return null;
  let side = yourSide;
  if (entry.game_id === 'Gomoku') {
    const state = entry.state as GomokuState | null;
    if (!state || state.phase !== 'Main' || (state.black_seat !== 'First' && state.black_seat !== 'Second')) return null;
    const color = colorForSeat(state, yourSide);
    if (!color) return null;
    side = color;
  }
  const first = side === 'First';
  const tone = first ? 'black' : 'white';
  const label = first ? '黒' : '白';
  switch (entry.game_id) {
    case 'Renju': case 'Jungo': case 'Connect6': case 'Gomoku':
      return { vessel: 'bowl', tool: 'stone', tone, label };
    case 'Chess': case 'Amazons':
      return { vessel: 'tray', tool: entry.game_id === 'Chess' ? 'pawn' : 'queen', tone: first ? 'white' : 'black', label: first ? '白' : '黒' };
    case 'Reversi': return { vessel: 'tray', tool: 'disc', tone, label };
    case 'Abalone': return { vessel: 'tray', tool: 'marble', tone, label };
    case 'Draughts': return { vessel: 'tray', tool: 'disc', tone: first ? 'white' : 'black', label: first ? '白' : '黒' };
    case 'TwixT': return { vessel: 'tray', tool: 'peg', tone, label };
    case 'Trax': return { vessel: 'tile', tool: 'line', tone: first ? 'white' : 'red', label: first ? '白' : '赤' };
    default: return null;
  }
}

const STONE_PATTERN = [[67,80],[80,73],[92,84],[76,96],[91,103]];

/** The clipped decorative stone texture is fixed, not a reserve or count. */
export function BoardSideLabel({ entry, yourSide, compact = false }: { entry: BoardEntry; yourSide: Side | null; compact?: boolean }) {
  const sample = boardSideLabel(entry, yourSide);
  if (!sample) return null;
  return <span className="board-own-side" role="img"
    data-testid="board-own-side" data-game={entry.game_id} data-side={yourSide} data-label={sample.label} data-tone={sample.tone} data-compact={compact}
    aria-label={`あなたの${sample.label}・道具の色見本`}>
    <span className={`own-vessel own-vessel--${sample.vessel}`} aria-hidden="true">
      <span className={`own-tool own-tool--${sample.tool} own-tool--${sample.tone}`}>
        {entry.game_id === 'Jungo' ? null : sample.tool === 'stone' ? STONE_PATTERN.map(([x, y], i) => <i key={`${x}-${y}`} className={`scene-stone scene-stone--${sample.tone}`} style={{left:`${x}%`,top:`${y}%`,rotate:`${i * 17}deg`}} />) :
          (sample.tool === 'pawn' || sample.tool === 'queen') ? <ChessPieceGlyph kind={sample.tool === 'pawn' ? 'Pawn' : 'Queen'} owner={sample.tone === 'white' ? 'First' : 'Second'} size={100} /> : <i className={`scene-piece scene-piece--${sample.tool} scene-stone--${sample.tone}`} />}
      </span>
    </span>
  </span>;
}
